const MORE_BTN_WIDTH = 66;

class ServiceTabsInstance {
  constructor(el) {
    this.el = el;
    this.nav = el.querySelector(".tabs__nav");
    this.openers = el.querySelector(".tabs__openers");
    this.indicator = el.querySelector(".tabs__indicator");
    this.moreBtn = el.querySelector(".tabs__more-btn");
    this.dropdown = el.querySelector(".tabs__dropdown");
    this.buttons = [...el.querySelectorAll(".tabs__openers .tab-opener")];

    // Active tab tracked by data-tab value, not index
    const activeBtn = el.querySelector(".tab-opener.active");
    this.activeTab = activeBtn?.dataset.tab || this.buttons[0]?.dataset.tab;
    // Index is still needed for the sliding indicator position
    this.activeIndex = this.buttons.findIndex(
      (b) => b.dataset.tab === this.activeTab,
    );

    this._dropdownOpen = false;

    this._init();
  }

  _init() {
    this._updateIndicator(false);
    this._syncPanels();
    this._updateOverflow();
    this._bindEvents();

    this._ro = new ResizeObserver(() => {
      this._updateIndicator(false);
      this._updateOverflow();
    });
    this._ro.observe(this.nav);
  }

  // ─── Panel lookup

  _getPanel(tabId) {
    if (!tabId) return null;
    return this.el.querySelector(`[data-tab-panel="${tabId}"]`);
  }

  // ─── Tab activation

  _activateTab(index) {
    const nextTab = this.buttons[index]?.dataset.tab;
    if (!nextTab || nextTab === this.activeTab) {
      this._closeDropdown();
      return;
    }

    const prevTab = this.activeTab;
    const prevIndex = this.activeIndex;

    // Update button states
    this.buttons[prevIndex]?.classList.remove("active");
    this.buttons[index].classList.add("active");

    this.activeTab = nextTab;
    this.activeIndex = index;

    this._updateIndicator(true);
    this._animatePanels(prevTab, nextTab);
    this._scrollTabIntoView(this.buttons[index]);
    this._closeDropdown();

    setTimeout(() => this._refreshDropdownItems(), 400);
  }

  // Called once on init — show active panel, hide the rest, no animation
  _syncPanels() {
    this.el.querySelectorAll("[data-tab-panel]").forEach((panel) => {
      const isActive = panel.dataset.tabPanel === this.activeTab;
      panel.classList.toggle("is-panel-active", isActive);
    });
  }

  // Called on every switch — hides old panel, slides in new from the correct side
  _animatePanels(fromTabId, toTabId) {
    const fromPanel = this._getPanel(fromTabId);
    const toPanel = this._getPanel(toTabId);

    // Direction: right if moving to a tab further in the list, left otherwise
    const fromIndex = this.buttons.findIndex(
      (b) => b.dataset.tab === fromTabId,
    );
    const enterClass =
      this.activeIndex < fromIndex ? "is-entering-right" : "is-entering-left";

    if (fromPanel) {
      fromPanel.classList.remove(
        "is-panel-active",
        "is-entering-right",
        "is-entering-left",
      );
    }

    if (!toPanel) return;

    toPanel.classList.add("is-panel-active");
    toPanel.offsetHeight; // force reflow so animation starts from `from` state
    toPanel.classList.add(enterClass);

    toPanel.addEventListener(
      "animationend",
      () => toPanel.classList.remove("is-entering-right", "is-entering-left"),
      {once: true},
    );
  }

  // ─── Indicator

  _updateIndicator(animate) {
    const btn = this.buttons[this.activeIndex];
    if (!btn || !this.indicator) return;

    if (!animate) {
      this.indicator.style.transition = "none";
      this.indicator.getBoundingClientRect(); // force reflow
    } else {
      this.indicator.style.transition = "";
    }

    this.indicator.style.left = btn.offsetLeft + "px";
    this.indicator.style.width = btn.offsetWidth + "px";

    if (!animate) {
      requestAnimationFrame(() => {
        this.indicator.style.transition = "";
      });
    }
  }

  // ─── Overflow & dropdown

  _updateOverflow() {
    if (!this.moreBtn) return;

    this.openers.style.paddingRight = "";
    const hasOverflow = this.openers.scrollWidth > this.openers.clientWidth + 1;

    if (!hasOverflow) {
      this.moreBtn.style.display = "none";
      this._closeDropdown();
      return;
    }

    this.openers.style.paddingRight = MORE_BTN_WIDTH + "px";
    this.moreBtn.style.display = "flex";
    this._refreshDropdownItems();
  }

  _refreshDropdownItems() {
    if (!this.dropdown || this.moreBtn?.style.display === "none") return;

    const scrollLeft = this.openers.scrollLeft;
    const visibleRight = scrollLeft + this.openers.clientWidth - MORE_BTN_WIDTH;

    const hiddenBtns = this.buttons.filter((btn) => {
      const left = btn.offsetLeft;
      const right = left + btn.offsetWidth;
      return left < scrollLeft || right > visibleRight;
    });

    this.dropdown.innerHTML = "";
    hiddenBtns.forEach((btn) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "tabs__dropdown-item";
      item.textContent = btn.textContent.trim();
      if (btn.dataset.tab === this.activeTab) item.classList.add("active");
      item.addEventListener("click", () =>
        this._activateTab(this.buttons.indexOf(btn)),
      );
      this.dropdown.appendChild(item);
    });
  }

  // ─── Scroll into view

  _scrollTabIntoView(btn) {
    const moreBtnVisible = this.moreBtn?.style.display !== "none";
    const effectiveWidth =
      this.openers.clientWidth - (moreBtnVisible ? MORE_BTN_WIDTH : 0);
    const scrollLeft = this.openers.scrollLeft;
    const btnLeft = btn.offsetLeft;
    const btnRight = btnLeft + btn.offsetWidth;
    const gap = 32;

    if (btnLeft < scrollLeft) {
      this.openers.scrollTo({
        left: Math.max(0, btnLeft - gap),
        behavior: "smooth",
      });
    } else if (btnRight > scrollLeft + effectiveWidth) {
      this.openers.scrollTo({
        left: btnRight - effectiveWidth + gap,
        behavior: "smooth",
      });
    }
  }

  // ─── Dropdown open/close

  _openDropdown() {
    this._refreshDropdownItems();
    this._dropdownOpen = true;
    this.dropdown.hidden = false;
    this.moreBtn.setAttribute("aria-expanded", "true");
  }

  _closeDropdown() {
    if (!this._dropdownOpen) return;
    this._dropdownOpen = false;
    if (this.dropdown) this.dropdown.hidden = true;
    this.moreBtn?.setAttribute("aria-expanded", "false");
  }

  _toggleDropdown() {
    this._dropdownOpen ? this._closeDropdown() : this._openDropdown();
  }

  // ─── Events

  _bindEvents() {
    this.buttons.forEach((btn, i) => {
      btn.addEventListener("click", () => this._activateTab(i));
    });

    if (this.moreBtn) {
      this.moreBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this._toggleDropdown();
      });
    }

    document.addEventListener("click", (e) => {
      const isInsideToggle =
        this.moreBtn?.contains(e.target) || this.dropdown?.contains(e.target);
      if (!isInsideToggle) this._closeDropdown();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this._closeDropdown();
    });

    this.openers.addEventListener(
      "scroll",
      () => {
        if (this.moreBtn?.style.display !== "none") {
          this._refreshDropdownItems();
        }
      },
      {passive: true},
    );
  }
}

const ServiceTabs = () => {
  document
    .querySelectorAll(".tabs")
    .forEach((el) => new ServiceTabsInstance(el));
};

export default ServiceTabs;
